import { createContextApi, invalidatesOne, providesOne, providesList, createCursorPagedEndpoint } from '@quickbee/store';
import { getContextClient, ApiError } from '@quickbee/api-client';

// src/index.ts
var IDENTITY_TAGS = ["Me", "Session", "Kyc", "User", "Delegation"];
var identityEndpoints = (build) => ({
  // ─── /me ────────────────────────────────────────────────────────────────────
  getMe: build.query({
    query: () => ({ url: "/me" }),
    providesTags: [{ type: "Me", id: "SELF" }]
  }),
  updateMe: build.mutation({
    query: ({ clientRequestId, ...body }) => ({
      url: "/me",
      method: "PUT",
      body,
      // `PUT /me` is @IdempotentRequest. A key derived from the body would be
      // wrong in the other direction: renaming A → B → A would replay the second
      // response and silently skip the third write.
      idempotencyKey: `me-update:${clientRequestId}`
    }),
    invalidatesTags: [{ type: "Me", id: "SELF" }]
  }),
  /**
   * The signed-in user's own password change. A wrong current password answers `PASSWORD_INVALID`
   * (the same refusal a wrong login gives), a new password the pool refuses answers
   * `PASSWORD_TOO_WEAK`. `204`, no tokens, nothing to invalidate — no cached read projects a
   * password.
   *
   * Keyed per intent: a second change of the same account within the window is a new change,
   * not a replay, so the key is the caller's draft id rather than anything derived from the body.
   */
  changeMyPassword: build.mutation({
    query: ({ clientRequestId, ...body }) => ({
      url: "/me/password",
      method: "POST",
      body,
      idempotencyKey: `me-password:${clientRequestId}`
    })
  }),
  listMySessions: build.query({
    query: () => ({ url: "/me/sessions" }),
    providesTags: (result) => providesList("Session", result)
  }),
  revokeSession: build.mutation({
    query: (sessionId) => ({
      url: `/me/sessions/${sessionId}`,
      method: "DELETE",
      // Revoking the same session twice is the same intent, so the session id is
      // itself the stable key.
      idempotencyKey: `session-revoke:${sessionId}`
    }),
    invalidatesTags: (_r, _e, sessionId) => invalidatesOne("Session", sessionId)
  }),
  /**
   * Revokes every session **including the caller's own** — the backend does not
   * exempt it. Treat a success as a logout and let `@quickbee/auth` tear the
   * session down rather than leaving the UI on a screen whose next call 401s.
   */
  revokeAllSessions: build.mutation({
    query: ({ clientRequestId }) => ({
      url: "/me/sessions/revoke-all",
      method: "POST",
      idempotencyKey: `session-revoke-all:${clientRequestId}`
    }),
    invalidatesTags: [{ type: "Session", id: "LIST" }]
  }),
  // ─── /kyc ───────────────────────────────────────────────────────────────────
  /**
   * ⚠️ `404 NOT_FOUND` is the normal answer for an account that has never
   * submitted KYC — `GetKycStatusQueryHandler` throws `NotFoundException("KycProfile")`
   * rather than returning a `NOT_STARTED` row. A KYC banner must read the 404 as
   * "not started", not as an error toast.
   */
  getMyKycStatus: build.query({
    query: () => ({ url: "/kyc/status" }),
    providesTags: [{ type: "Kyc", id: "SELF" }]
  }),
  /** Same 404-means-not-started caveat as {@link getMyKycStatus}. Requires `kyc:read:tenant`. */
  getUserKycStatus: build.query({
    query: (userId) => ({ url: `/kyc/${userId}/status` }),
    providesTags: (_r, _e, userId) => providesOne("Kyc", userId)
  }),
  /**
   * The KYC review queue (B1) — who is waiting on {@link decideKyc}, oldest first.
   * Requires `kyc:read:tenant`.
   *
   * Tagged by `userId` rather than `id`, because that is the row's identity here;
   * {@link decideKyc}'s `invalidatesOne('Kyc', userId)` therefore drops both the
   * decided row and the list, and a decided submission leaves the queue without a
   * manual refetch.
   */
  listKycSubmissions: build.query({
    query: (args) => ({ url: "/kyc/submissions", params: args ?? {} }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("Kyc", result, "userId"),
    keepUnusedDataFor: 30
  }),
  /**
   * The same queue, paged **discretely** — one request, one page, no accumulation.
   *
   * {@link listKycSubmissions} drops `cursor` from its cache key and appends every response
   * into a single entry, which is exactly right for a "load more" list and exactly wrong for a
   * numbered pager: paging forward would grow the rendered set instead of replacing it, and
   * paging *back* would re-fetch a cursor already walked and append its rows a second time.
   *
   * So this one keeps the cursor in the cache key. Each page is its own entry, which also makes
   * *Previous* free — a page already visited is answered from cache rather than re-fetched.
   *
   * Both exist because both are legitimate, and this is the same pair, for the same stated
   * reason, as api-audit's `listEntries` / `listEntriesPage`. The review panel
   * (`@quickbee/kyc-review`) reads this one: the queue is a work list somebody pages through,
   * and it sits under a card showing the queue's real total.
   */
  listKycSubmissionsPage: build.query({
    query: (args) => ({ url: "/kyc/submissions", params: args ?? {} }),
    providesTags: (result) => providesList("Kyc", result, "userId"),
    keepUnusedDataFor: 30
  }),
  /**
   * What the reviewer on {@link decideKyc} actually reviews (B2). Requires
   * `kyc:read:tenant` — never `:own`.
   *
   * **Not cached beyond the screen.** `imageUrl` is a presigned link with a TTL in
   * minutes, so a cache entry outlives the URL inside it and would render as a
   * broken image rather than a refetch. Reading this is also an audited, sensitive
   * access on the backend — re-reading on mount is the honest behaviour, not a
   * cost to optimise away.
   *
   * `404` means the account has no KYC profile at all, which is distinct from
   * `200 []` ("profile exists, no documents"); treat it as the state it is.
   */
  getKycDocuments: build.query({
    query: (userId) => ({ url: `/kyc/${userId}/documents` }),
    providesTags: (_r, _e, userId) => providesOne("Kyc", userId),
    keepUnusedDataFor: 0
  }),
  /**
   * The WORM decision trail for one case (F-29). Requires `kyc:read:tenant`.
   *
   * The table was written on every decision and read by exactly one query — the
   * idempotency existence check — so "how many times has this customer been refused,
   * and why" was answerable only by direct database access, on the one table designed
   * to answer it.
   *
   * Not cached beyond the screen, for the same reason as {@link getKycDocuments}: the
   * read is audited on the backend, because "who looked at whose compliance history"
   * has to be answerable. Re-reading on mount is the honest behaviour.
   */
  getKycDecisionHistory: build.query({
    query: (userId) => ({ url: `/kyc/${userId}/decisions` }),
    providesTags: (_r, _e, userId) => providesOne("Kyc", userId),
    keepUnusedDataFor: 0
  }),
  /**
   * Takes the soft claim on a case when the drawer opens (F-27), so a second reviewer
   * sees it is taken before they read the file rather than after they have filled in a
   * decision the first already recorded.
   *
   * `skipIdempotencyKey`: the backend route is not `@IdempotentRequest`. Claiming twice
   * is already a no-op that refreshes your own hold — which is what a drawer left open
   * for a while should do — and requiring a key on a screen-open would make every
   * drawer render generate one.
   *
   * Answers `409` when somebody else holds it; the caller renders that, it is not an error.
   */
  claimKycReview: build.mutation({
    query: ({ userId }) => ({
      url: `/kyc/${userId}/claim`,
      method: "POST",
      skipIdempotencyKey: true
    }),
    invalidatesTags: [{ type: "Kyc", id: "LIST" }]
  }),
  releaseKycReview: build.mutation({
    query: ({ userId }) => ({
      url: `/kyc/${userId}/claim/release`,
      method: "POST",
      skipIdempotencyKey: true
    }),
    invalidatesTags: [{ type: "Kyc", id: "LIST" }]
  }),
  /**
   * The upload rules, so a capture form can refuse a file before the customer waits for it
   * (F-37). Long-lived in cache: a size cap changes with a deployment, not with a session.
   */
  getKycDocumentPolicy: build.query({
    query: () => ({ url: "/kyc/documents/policy" }),
    keepUnusedDataFor: 3600
  }),
  // ── KYC scan upload (three-leg; design D3) ─────────────────────────────────
  // Leg 2 (the PUT to storage) is not here — it is `uploadToPresignedUrl` from
  // `@quickbee/api-client`, fed by `toKycUploadTicket`. `requiredHeaders` needs the
  // adapter because the backend names the field differently from the helper's ticket.
  requestKycDocumentUpload: build.mutation({
    query: ({ draftId, ...body }) => ({
      url: "/kyc/documents/upload-url",
      method: "POST",
      body,
      // Scoped to the capture attempt, not the user: an entity-only key would make the second
      // document's ticket replay the first — the idempotency-key-scope trap.
      idempotencyKey: `kyc-doc-upload:${draftId}`
    })
    // Invalidates nothing: leg 1 creates a PENDING row no list shows.
  }),
  confirmKycDocumentUpload: build.mutation({
    query: ({ documentId, requestId }) => ({
      url: `/kyc/documents/${documentId}/confirm`,
      method: "POST",
      idempotencyKey: `kyc-doc-confirm:${documentId}:${requestId}`
    })
  }),
  submitKyc: build.mutation({
    query: ({ clientRequestId, ...body }) => ({
      url: "/kyc/submissions",
      method: "POST",
      body,
      // I9: stable across user retries. Two taps on "Submit" must not create two
      // submissions competing for one decision.
      idempotencyKey: `kyc-submit:${clientRequestId}`
    }),
    invalidatesTags: [
      { type: "Kyc", id: "SELF" },
      { type: "Me", id: "SELF" }
    ]
  }),
  // ── Assisted registration & on-behalf KYC (design D9) ─────────────────────
  // Staff/agent surface: the target saver rides the path; the caller's own token authorizes.
  // No session is ever minted on the assisting device.
  registerSaverAssisted: build.mutation({
    query: ({ draftId, ...body }) => ({
      url: "/users/savers",
      method: "POST",
      body,
      // One key per wizard session: a retried submit replays, an edited form re-registers.
      idempotencyKey: `assist-register:${draftId}`
    }),
    invalidatesTags: [{ type: "User", id: "LIST" }]
  }),
  verifySaverPhoneAssisted: build.mutation({
    query: ({ userId, otp, requestId }) => ({
      url: `/users/savers/${userId}/verify-phone`,
      method: "POST",
      body: { otp },
      // Per attempt, not per user: a wrong OTP must be retryable with a fresh code.
      idempotencyKey: `assist-verify:${userId}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { userId }) => invalidatesOne("User", userId)
  }),
  /**
   * Re-sends the registration passcode to the CUSTOMER's phone (F-11).
   *
   * `POST /users/savers` is `@IdempotentRequest`, so retrying the registration replays its
   * cached response and sends nothing — this is the only leg that puts a second code on the
   * customer's phone. Before it existed, a passcode that did not arrive ended the visit: the
   * desk had no way to send another, and the half-made account held the number for thirty days.
   *
   * The `attempt` is part of the idempotency key, the same way `sendAgentPhoneOtp` does it: a
   * double-click replays the first send rather than putting two codes on the phone, while a
   * deliberate second press — a fresh `attempt` — reaches the handler. A key fixed per user would
   * cache the first response forever and send nothing after it, which is precisely the failure
   * this endpoint exists to fix.
   *
   * What bounds it is the challenge's own cooldown and attempt cap, not the key: a press inside
   * the window is a `429` whose `Retry-After` the wizard renders as a countdown.
   */
  resendSaverOtpAssisted: build.mutation({
    query: ({ userId, attempt }) => ({
      url: `/users/savers/${userId}/resend-otp`,
      method: "POST",
      idempotencyKey: `assist-otp-resend:${userId}:${attempt}`
    })
  }),
  /**
   * Deletes an assisted registration that never verified, freeing the customer's phone number
   * immediately (F-11).
   *
   * The desk's way out when a passcode cannot be delivered at all — a wrong digit taken down, a
   * phone that is off, a number that turns out to belong to someone else. Without it the number
   * stayed held by the unverified-account sweeper for thirty days, which locked the customer out
   * of *every* registration route, self-service included, over a typo.
   *
   * The service refuses anything that has already verified, so this can never become a
   * delete-customer button.
   */
  abandonSaverRegistration: build.mutation({
    query: ({ userId }) => ({
      url: `/users/savers/${userId}`,
      method: "DELETE",
      // Not an `@IdempotentRequest` route, so there is no key for the filter to read. Deleting
      // the same unverified registration twice is the same intent and the second call simply
      // finds nothing; the wizard resets to step one on success, so there is no second press to
      // protect against either.
      skipIdempotencyKey: true
    }),
    invalidatesTags: [{ type: "User", id: "LIST" }]
  }),
  requestKycDocumentUploadAssisted: build.mutation({
    query: ({ userId, draftId, ...body }) => ({
      url: `/kyc/${userId}/documents/upload-url`,
      method: "POST",
      body,
      idempotencyKey: `kyc-doc-upload:${draftId}`
    })
  }),
  confirmKycDocumentUploadAssisted: build.mutation({
    query: ({ userId, documentId, requestId }) => ({
      url: `/kyc/${userId}/documents/${documentId}/confirm`,
      method: "POST",
      idempotencyKey: `kyc-doc-confirm:${documentId}:${requestId}`
    })
  }),
  submitKycAssisted: build.mutation({
    query: ({ userId, clientRequestId, ...body }) => ({
      url: `/kyc/${userId}/submissions`,
      method: "POST",
      body,
      idempotencyKey: `kyc-submit:${clientRequestId}`
    }),
    invalidatesTags: (_r, _e, { userId }) => invalidatesOne("User", userId)
  }),
  /**
   * In-app KYC approved/rejected notices for the signed-in customer (F-22).
   *
   * This is the guaranteed channel: email is optional and WhatsApp needs a tenant Meta
   * binding. Requires `kyc:read:own`. Not cached beyond the screen — a decision that
   * lands while the inbox is open should appear on the next look, not after a stale TTL.
   */
  listMyKycNotices: build.query({
    query: () => ({ url: "/kyc/notices" }),
    providesTags: [{ type: "Kyc", id: "NOTICES" }],
    keepUnusedDataFor: 30
  }),
  /**
   * Marks one of the caller's KYC decision notices as read. Requires `kyc:read:own`.
   * Idempotent on the notice id: marking the same notice twice is the same intent.
   */
  markKycNoticeRead: build.mutation({
    query: ({ id }) => ({
      url: `/kyc/notices/${id}/read`,
      method: "POST",
      idempotencyKey: `kyc-notice-read:${id}`
    }),
    invalidatesTags: [{ type: "Kyc", id: "NOTICES" }]
  }),
  /**
   * Customers who were decided and who have neither an email nor a phone WhatsApp can
   * use — a desk should follow up by phone (F-22). Open rows by default. Requires
   * `kyc:read:tenant`.
   *
   * Not cached beyond the screen: a newly unreachable decision should show up the
   * moment a reviewer looks, not after a stale TTL.
   */
  listUndeliverableKycNotifications: build.query({
    query: (args) => ({
      url: "/kyc/undeliverable-notifications",
      params: args ?? {}
    }),
    providesTags: [{ type: "Kyc", id: "UNDELIVERABLE" }],
    keepUnusedDataFor: 0
  }),
  /**
   * Records that somebody has followed up on an unreachable KYC notification, and what
   * they did. The note is required. Requires `kyc:decide:tenant`. Idempotent on the
   * row id: resolving twice is the same follow-up, not a second one.
   */
  resolveUndeliverableKycNotification: build.mutation({
    query: ({ id, note }) => ({
      url: `/kyc/undeliverable-notifications/${id}/resolve`,
      method: "POST",
      body: { note },
      idempotencyKey: `kyc-undeliverable-resolve:${id}`
    }),
    invalidatesTags: [{ type: "Kyc", id: "UNDELIVERABLE" }]
  }),
  decideKyc: build.mutation({
    query: ({ userId, clientRequestId, ...body }) => ({
      url: `/kyc/${userId}/decision`,
      method: "POST",
      body,
      // The controller reads `X-Idempotency-Key` itself and carries it into
      // `RecordKycDecisionCommand`, so this value becomes the decision's dedupe
      // identity in the domain — not merely a filter-level replay guard. One id
      // per reviewer action; reusing it for a later re-review would be ignored.
      idempotencyKey: `kyc-decision:${userId}:${clientRequestId}`
    }),
    invalidatesTags: (_r, _e, { userId }) => [
      ...invalidatesOne("Kyc", userId),
      ...invalidatesOne("User", userId),
      { type: "Kyc", id: "UNDELIVERABLE" },
      { type: "Kyc", id: "NOTICES" }
    ]
  }),
  // ─── /users ─────────────────────────────────────────────────────────────────
  /**
   * Genuinely paged, and `pagination.total` is a real count.
   *
   * It was first-page-only for a long time: `SearchUsersQueryHandler` pinned the query
   * to Spring page 0, the controller built its envelope with `Page.ofOffset(…, offset =
   * 0)`, and `total` was synthesised as `size + 1` whenever a page came back full. Every
   * directory walk on the platform — tenant-service's roster reconciliation, helpdesk's
   * agent directory — therefore stopped after one page, and any tenant past `size`
   * accounts was silently truncated. The repository now resolves the offset out of the
   * cursor and runs a count query.
   */
  listUsers: build.query({
    // Takes `SearchUsersArgs` rather than `SearchUsersArgs | void`: every field is optional, so
    // `useListUsersQuery({})` is the no-filter call, and the cursor helper below cannot be typed
    // against an argument that may be `void`.
    query: (args) => ({ url: "/users", params: args ?? {} }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("User", result)
  }),
  /**
   * The same read, paged **discretely** — one request, one page, no accumulation.
   *
   * {@link listUsers} drops `cursor` from its cache key and appends every response into a
   * single entry, which is right for a "load more" list and wrong for a numbered pager: paging
   * forward would grow the rendered set rather than replace it, and paging *back* would re-fetch
   * a walked cursor and append its rows a second time. This one keeps the cursor in the cache
   * key, so each page is its own entry and Previous is answered from cache.
   *
   * Same pair, same reason, as `api-audit`'s `listEntries`/`listEntriesPage`.
   *
   * `pagination.total` is a real count here (the repository runs a count query), so a numbered
   * pager over this endpoint shows the tenant's own figure rather than the window's.
   */
  listUsersPage: build.query({
    query: (args) => ({ url: "/users", params: args ?? {} }),
    providesTags: (result) => providesList("User", result)
  }),
  /**
   * The savers the calling agent registered — the Partner app's collection picker
   * (multi-channel collections §5.3 item 5 / G19).
   *
   * Not `listUsers` with a filter: that endpoint is the tenant's user directory and needs
   * `users:read:tenant`, which an `AGENT` can never satisfy. Widening the directory to
   * `:partner` would have handed every agent the whole book, so this is its own read with
   * the containment applied server-side against the caller's own agent linkage. A caller with
   * no linkage gets an **empty page**, never an unfiltered one.
   *
   * The phone comes back masked. An agent picking a customer they signed up does not need the
   * number read back to them, and a picker on a shared device is a shoulder-surfing surface.
   */
  listMySavers: build.query({
    // Non-void argument for the same reason `listUsers` takes one: the cursor helper's
    // `serializeQueryArgs` cannot be typed against an argument that may be `void`, so
    // `useListMySaversQuery({})` is the no-filter call.
    query: (args) => ({ url: "/users/savers", params: args ?? {} }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("User", result, "customerId"),
    keepUnusedDataFor: 120
  }),
  getUser: build.query({
    query: (id) => ({ url: `/users/${id}` }),
    providesTags: (_r, _e, id) => providesOne("User", id)
  }),
  provisionPartnerUser: build.mutation({
    query: ({ clientRequestId, ...body }) => ({
      url: "/users/partner",
      method: "POST",
      body,
      idempotencyKey: `user-partner:${clientRequestId}`
    }),
    invalidatesTags: [{ type: "User", id: "LIST" }]
  }),
  provisionPlatformUser: build.mutation({
    query: ({ clientRequestId, ...body }) => ({
      url: "/users/platform",
      method: "POST",
      body,
      idempotencyKey: `user-platform:${clientRequestId}`
    }),
    invalidatesTags: [{ type: "User", id: "LIST" }]
  }),
  /**
   * A role assignment is **reversible** — assign → revoke → re-assign is a
   * legitimate sequence, and `AssignRoleCommandHandler` executes each one. A key
   * built from `(userId, role, scope, orgScopeId)` is stable across those three
   * calls, so inside the filter's 24 h window the third would replay the first
   * one's cached profile and never reach the handler. The caller mints one id
   * per committed assignment instead.
   *
   * `branchCodes` is the branch half of the grant (design §3.3): required and non-empty
   * for `scope: 'BRANCH'`, rejected for every other scope. Sending it is the only way a
   * console can create a branch-pinned holder — the API has accepted it since the grant
   * became the carrier of scope, and until the admin drawer nothing could send it.
   */
  assignRole: build.mutation({
    query: ({ userId, clientRequestId, ...body }) => ({
      url: `/users/${userId}/roles`,
      method: "POST",
      body,
      idempotencyKey: `role-assign:${userId}:${clientRequestId}`
    }),
    invalidatesTags: (_r, _e, { userId }) => invalidatesOne("User", userId)
  }),
  /** Reversible in the same way as {@link assignRole}; same keying rule. */
  revokeRole: build.mutation({
    query: ({ userId, role, clientRequestId }) => ({
      url: `/users/${userId}/roles/${role}`,
      method: "DELETE",
      idempotencyKey: `role-revoke:${userId}:${clientRequestId}`
    }),
    invalidatesTags: (_r, _e, { userId }) => invalidatesOne("User", userId)
  }),
  /**
   * `ACTIVE → SUSPENDED`, and its inverse is {@link reactivateUser} — the pair
   * cycles. `user-suspend:{id}` would make the second suspension of the same
   * account within 24 h a silent no-op that returns the first one's body, so the
   * console would show `SUSPENDED` for an account the backend left `ACTIVE`.
   */
  suspendUser: build.mutation({
    query: ({ userId, clientRequestId, reason }) => ({
      url: `/users/${userId}/suspend`,
      method: "POST",
      // `@RequestBody(required = false)`, and `reason` is optional — send an
      // empty object rather than `{ reason: undefined }` so nothing depends on
      // how the transport happens to serialise `undefined`.
      body: reason === void 0 ? {} : { reason },
      idempotencyKey: `user-suspend:${userId}:${clientRequestId}`
    }),
    invalidatesTags: (_r, _e, { userId }) => invalidatesOne("User", userId)
  }),
  /**
   * Re-points (or clears, on a null `agentCode`) a saver's commission attribution
   * (design D11). PUT semantics — naturally idempotent, no key. The service validates the
   * code against an ACTIVE agent synchronously and refuses the edit when the agent
   * directory is unreachable; `registeredBy*` provenance is never touched, and
   * partner-service never re-points an already-CONVERTED referral.
   */
  updateAgentAttribution: build.mutation({
    query: ({ userId, agentCode }) => ({
      url: `/users/${userId}/agent-attribution`,
      method: "PUT",
      body: agentCode === null ? {} : { agentCode },
      skipIdempotencyKey: true
    }),
    invalidatesTags: (_r, _e, { userId }) => invalidatesOne("User", userId)
  }),
  /** The other half of the cycle; same keying rule as {@link suspendUser}. */
  reactivateUser: build.mutation({
    query: ({ userId, clientRequestId }) => ({
      url: `/users/${userId}/reactivate`,
      method: "POST",
      idempotencyKey: `user-reactivate:${userId}:${clientRequestId}`
    }),
    invalidatesTags: (_r, _e, { userId }) => invalidatesOne("User", userId)
  }),
  /**
   * Asks the identity provider to send this user a one-time reset code and require a new
   * password at their next sign-in — an administrator starting a reset for one of their
   * tenant's people.
   *
   * The response carries a sentence and nothing else. There is no code, no delivery address
   * and no password in it, because an administrator who could read any of those could sign in
   * as the user they just "helped" — so this is safe to put behind a tenant-scoped grant.
   *
   * Keyed per click, like {@link suspendUser} and for the same reason: resets repeat. A stable
   * `user-password-reset:{id}` would make the second reset of the same account within the
   * idempotency window a silent no-op, so an administrator retrying after a user said the code
   * never arrived would get a success toast and no second code.
   *
   * Invalidates nothing: no account field changes, the reset state lives at the identity
   * provider, and the roster does not project it.
   */
  resetUserPassword: build.mutation({
    query: ({ userId, clientRequestId }) => ({
      url: `/users/${userId}/password-reset`,
      method: "POST",
      idempotencyKey: `user-password-reset:${userId}:${clientRequestId}`
    })
  }),
  /**
   * Self-service "I forgot my password" — unauthenticated.
   *
   * Always resolves, and always with the same body. The service answers `202` whether or not
   * the username names a real account, because any difference an anonymous caller could observe
   * is a way to test which addresses are registered. Callers must render the response as-is and
   * must not add a "no such account" branch of their own.
   */
  requestPasswordReset: build.mutation({
    query: ({ username }) => ({
      url: "/auth/password-reset",
      method: "POST",
      body: { username },
      // No key, matching the controller, which carries no `@IdempotentRequest`. Replaying a reset
      // request is the wrong behaviour — the second attempt exists because the first code never
      // arrived — and the filter keys on a tenant this caller may not have.
      skipIdempotencyKey: true
    })
  }),
  /**
   * The second leg: the code the user received plus the password they chose. `204`, and
   * **no tokens** — holding a reset code proves access to a mailbox, not to an account, so the
   * user signs in afterwards with the password they just set.
   *
   * Unlike the initiate leg this one does distinguish its failures: the caller has already
   * proved they hold the code, so telling them the code expired versus the password being too
   * weak reveals nothing they did not already know.
   */
  confirmPasswordReset: build.mutation({
    query: ({ username, code, newPassword }) => ({
      url: "/auth/password-reset/confirm",
      method: "POST",
      body: { username, code, newPassword },
      // The code is single-use at the identity provider, so a double submit fails the second time
      // on its own — there is nothing an idempotency key would protect.
      skipIdempotencyKey: true
    })
  }),
  /**
   * Initiates DPDP erasure. Returns `204` — the account is *not* re-read, so the
   * caller must refetch rather than expect a profile back. A stable key means a
   * double submit erases once.
   */
  eraseUser: build.mutation({
    query: ({ userId, reason }) => ({
      url: `/users/${userId}/erasure`,
      method: "POST",
      body: reason === void 0 ? {} : { reason },
      idempotencyKey: `user-erasure:${userId}`
    }),
    invalidatesTags: (_r, _e, { userId }) => invalidatesOne("User", userId)
  }),
  // ─── /delegation (marketing-engine-solution-design.md §4.5, WP-9) ───────────
  //
  // Two sides of one rail. The jeweller's three endpoints are `:own` — they act on the
  // caller's own tenant, which is never a parameter — and the operator's two are
  // `use:tenant`. No endpoint here takes a tenant id that decides WHOSE data comes back.
  /**
   * "Who have I let into my console." Requires `delegation-grants:read:own`, so a
   * TENANT_ADMIN only.
   */
  listDelegationGrants: build.query({
    query: (args) => ({
      url: "/delegation/grants",
      params: args ?? {}
    }),
    providesTags: [{ type: "Delegation", id: "GRANTS" }]
  }),
  /**
   * Issues a delegation. Idempotent on the caller's `grantId`, which the screen mints
   * once per dialog rather than once per click.
   *
   * A second live grant for the same pair is refused (`DELEGATION_NOT_PERMITTED`), not
   * silently replaced — the existing one has to be revoked first, so a jeweller never
   * ends up with two grants they think are one.
   */
  grantDelegation: build.mutation({
    query: (body) => ({
      url: "/delegation/grants",
      method: "POST",
      body,
      idempotencyKey: `delegation-grant:${body.grantId}`
    }),
    invalidatesTags: [{ type: "Delegation", id: "GRANTS" }]
  }),
  /**
   * Revokes a delegation.
   *
   * What "instantly" means: the next token mint for that pair is refused. A token already
   * in a browser keeps working until it expires — at most fifteen minutes, because a
   * delegation token carries no refresh and nothing on this platform keeps a blacklist.
   * Say that in the confirmation dialog rather than implying the session dies mid-click.
   */
  revokeDelegation: build.mutation({
    query: (grantId) => ({
      url: `/delegation/grants/${grantId}`,
      method: "DELETE",
      idempotencyKey: `delegation-revoke:${grantId}`
    }),
    invalidatesTags: [{ type: "Delegation", id: "GRANTS" }]
  }),
  /**
   * The jeweller's "who is in my console" panel — every delegation token minted against
   * their grants, the named human it was minted for, and the window it covers.
   *
   * `keepUnusedDataFor: 0` and a short poll are the honest settings for this one: a panel
   * that answers "who is in here right now" from a cache is answering a different
   * question.
   */
  listDelegationSessions: build.query({
    query: (args) => ({
      url: "/delegation/sessions",
      params: args ?? {}
    }),
    providesTags: [{ type: "Delegation", id: "SESSIONS" }],
    keepUnusedDataFor: 0
  }),
  /**
   * The operator's tenant switcher. Live grants only — every entry is one the mint will
   * honour. Requires `delegation-grants:use:tenant`.
   */
  listDelegableTenants: build.query({
    query: (args) => ({
      url: "/delegation/available",
      params: args ?? {}
    }),
    providesTags: [{ type: "Delegation", id: "AVAILABLE" }]
  }),
  /**
   * "Work as &lt;jeweller&gt;" — exchanges the caller's own token for a short-lived
   * second-issuer one scoped to the target tenant.
   *
   * `ownSession: true` is load-bearing and not an optimisation. Once a delegation is in
   * force, `@quickbee/api-client` sends the borrowed token on every delegable context —
   * and the backend refuses to mint a delegation token from a delegation token
   * (deliberately: chaining them would let A's grant spend a grant issued to A by
   * somebody else, with nobody in the chain having consented to the far end). Without
   * this flag the re-mint at the fifteen-minute mark would fail and drop the session.
   *
   * Not idempotency-keyed: two "work as" clicks are two mints and two rows in the
   * jeweller's panel, because that is what actually happened.
   */
  mintDelegationToken: build.mutation({
    query: (body) => ({
      url: "/delegation/token",
      method: "POST",
      body,
      ownSession: true,
      skipIdempotencyKey: true
    })
  })
  // ─── Deliberately absent ────────────────────────────────────────────────────
  // `GET /identity/customers/{id}/display-info` is not defined here. It is
  // `@PubliclyAccessible` in the sense of "no JWT", is reachable only inside the
  // VPC, and returns the customer's **full E.164 phone** for S2S matching. It has
  // no gateway route by design, so a browser call 404s at the edge — and if one
  // ever did reach it, the SDK would be handing unmasked PII to a client bundle.
  // Operator screens that need a name for a customer id must go through a
  // context that exposes a masked projection.
});

// src/upload.ts
function toKycUploadTicket(ticket) {
  return {
    uploadUrl: ticket?.uploadUrl,
    ...ticket?.requiredHeaders ? { headers: ticket.requiredHeaders } : {}
  };
}

// src/kycDocumentRules.ts
var SECOND_KYC_DOCUMENT_TYPES = [
  "PAN",
  "PASSPORT",
  "DRIVING_LICENCE",
  "VOTER_ID"
];
var KYC_DOCUMENT_RULES = {
  // Twelve digits, never starting 0 or 1 — UIDAI does not issue those. The server additionally
  // runs the Verhoeff checksum the number carries; `isKycIdentifierValid` deliberately stays
  // shape-only (it mirrors identity-service's DTO-level `@Pattern`, pinned by this file's own
  // tests), so a self-serve customer's real card is never rejected by a client bug. A caller that
  // wants the checksum too — an agent transcribing a card in front of the customer, where a
  // mistyped digit is exactly the kind of thing re-reading the card fixes — uses
  // {@link isAadhaarChecksumValid} alongside it.
  AADHAAR_NUMBER: {
    pattern: /^[2-9][0-9]{11}$/,
    maxLength: 12,
    // Starts 2, not 1: the placeholder has to be a shape the field would accept, or the first
    // thing a customer sees is an example of an invalid number. It also has to pass the Verhoeff
    // checksum itself (`isAadhaarChecksumValid('234567890124')` is `true`) — this was
    // `'2345 6789 0123'`, which *looks* identical but fails the checksum, so anyone who typed the
    // hint verbatim had every KYC submission bounced by the server's own check-digit rejection.
    example: "2345 6789 0124",
    requiresImage: true
  },
  PAN: { pattern: /^[A-Z]{5}[0-9]{4}[A-Z]$/, maxLength: 10, example: "ABCDE1234F", requiresImage: true },
  PASSPORT: { pattern: /^[A-Z][0-9]{7}$/, maxLength: 8, example: "A1234567", requiresImage: true },
  DRIVING_LICENCE: {
    pattern: /^[A-Z]{2}[0-9]{13}$/,
    maxLength: 15,
    example: "KL0120110012345",
    requiresImage: true
  },
  VOTER_ID: { pattern: /^[A-Z]{3}[0-9]{7}$/, maxLength: 10, example: "ABC1234567", requiresImage: true }
};
function normalizeKycIdentifier(raw) {
  return raw.replace(/[\s-]/g, "").toUpperCase();
}
function isKycIdentifierValid(type, raw) {
  return KYC_DOCUMENT_RULES[type].pattern.test(normalizeKycIdentifier(raw));
}
var VERHOEFF_D = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 2, 3, 4, 0, 6, 7, 8, 9, 5],
  [2, 3, 4, 0, 1, 7, 8, 9, 5, 6],
  [3, 4, 0, 1, 2, 8, 9, 5, 6, 7],
  [4, 0, 1, 2, 3, 9, 5, 6, 7, 8],
  [5, 9, 8, 7, 6, 0, 4, 3, 2, 1],
  [6, 5, 9, 8, 7, 1, 0, 4, 3, 2],
  [7, 6, 5, 9, 8, 2, 1, 0, 4, 3],
  [8, 7, 6, 5, 9, 3, 2, 1, 0, 4],
  [9, 8, 7, 6, 5, 4, 3, 2, 1, 0]
];
var VERHOEFF_P = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 5, 7, 6, 2, 8, 3, 0, 9, 4],
  [5, 8, 0, 3, 7, 9, 6, 1, 4, 2],
  [8, 9, 1, 6, 0, 4, 3, 5, 2, 7],
  [9, 4, 5, 3, 1, 2, 6, 8, 7, 0],
  [4, 2, 8, 6, 5, 7, 3, 9, 0, 1],
  [2, 7, 9, 3, 8, 0, 6, 4, 1, 5],
  [7, 0, 4, 6, 9, 1, 3, 2, 5, 8]
];
function cell(table, row, column) {
  return table[row]?.[column] ?? 0;
}
function isAadhaarChecksumValid(raw) {
  const digits = normalizeKycIdentifier(raw);
  if (!/^\d{12}$/.test(digits)) return false;
  let c = 0;
  for (let i = 0; i < digits.length; i += 1) {
    const digit = Number(digits[digits.length - 1 - i]);
    c = cell(VERHOEFF_D, c, cell(VERHOEFF_P, i % 8, digit));
  }
  return c === 0;
}
function failure(error) {
  if (error instanceof ApiError) {
    if (error.status >= 400 && error.status < 500) {
      return { kind: "refused", status: error.status, code: error.code, message: error.message };
    }
    return { kind: "unavailable", message: error.message };
  }
  return {
    kind: "unavailable",
    message: error instanceof Error ? error.message : "The request could not be completed."
  };
}
async function fetchMyKycStatus() {
  try {
    const client = getContextClient({ context: "identity" });
    const data = await client.get("/kyc/status");
    if (!data) {
      return { kind: "unavailable", message: "KYC status was not returned." };
    }
    return { kind: "ok", data };
  } catch (error) {
    return failure(error);
  }
}
async function fetchMySessions() {
  try {
    const client = getContextClient({ context: "identity" });
    const data = await client.get("/me/sessions");
    return { kind: "ok", data: data ?? [] };
  } catch (error) {
    return failure(error);
  }
}
async function revokeMySession(sessionId) {
  try {
    const client = getContextClient({ context: "identity" });
    await client.delete(`/me/sessions/${sessionId}`, { idempotencyKey: `session-revoke:${sessionId}` });
    return { kind: "ok", data: void 0 };
  } catch (error) {
    return failure(error);
  }
}
async function revokeAllMySessions(clientRequestId) {
  try {
    const client = getContextClient({ context: "identity" });
    await client.post("/me/sessions/revoke-all", void 0, {
      idempotencyKey: `session-revoke-all:${clientRequestId}`
    });
    return { kind: "ok", data: void 0 };
  } catch (error) {
    return failure(error);
  }
}
async function changeMyPassword(request, clientRequestId) {
  try {
    const client = getContextClient({ context: "identity" });
    await client.post("/me/password", request, { idempotencyKey: `me-password:${clientRequestId}` });
    return { kind: "ok", data: void 0 };
  } catch (error) {
    return failure(error);
  }
}
async function fetchDelegableTenants() {
  try {
    const client = getContextClient({ context: "identity" });
    const data = await client.get("/delegation/available");
    return { kind: "ok", data: data ?? [] };
  } catch (error) {
    return failure(error);
  }
}
async function mintDelegationToken(targetTenantId) {
  try {
    const client = getContextClient({ context: "identity" });
    const data = await client.post(
      "/delegation/token",
      { targetTenantId },
      { ownSession: true, skipIdempotencyKey: true }
    );
    return { kind: "ok", data };
  } catch (error) {
    return failure(error);
  }
}

// src/types.ts
var IDENTITY_MAX_PAGE_SIZE = 100;

// src/index.ts
var identityApi = createContextApi({
  context: "identity",
  reducerPath: "identityApi",
  tagTypes: IDENTITY_TAGS,
  endpoints: identityEndpoints
});
var {
  useGetMeQuery,
  useUpdateMeMutation,
  useChangeMyPasswordMutation,
  useListMySessionsQuery,
  useRevokeSessionMutation,
  useRevokeAllSessionsMutation,
  useGetMyKycStatusQuery,
  useGetUserKycStatusQuery,
  useListKycSubmissionsQuery,
  useListKycSubmissionsPageQuery,
  useGetKycDocumentPolicyQuery,
  useGetKycDocumentsQuery,
  useGetKycDecisionHistoryQuery,
  useClaimKycReviewMutation,
  useReleaseKycReviewMutation,
  useRequestKycDocumentUploadMutation,
  useConfirmKycDocumentUploadMutation,
  useSubmitKycMutation,
  useListMyKycNoticesQuery,
  useMarkKycNoticeReadMutation,
  useListUndeliverableKycNotificationsQuery,
  useResolveUndeliverableKycNotificationMutation,
  useDecideKycMutation,
  useListUsersQuery,
  useListUsersPageQuery,
  useListMySaversQuery,
  useGetUserQuery,
  useProvisionPartnerUserMutation,
  useProvisionPlatformUserMutation,
  useAssignRoleMutation,
  useRevokeRoleMutation,
  useSuspendUserMutation,
  useUpdateAgentAttributionMutation,
  useRegisterSaverAssistedMutation,
  useVerifySaverPhoneAssistedMutation,
  useResendSaverOtpAssistedMutation,
  useAbandonSaverRegistrationMutation,
  useRequestKycDocumentUploadAssistedMutation,
  useConfirmKycDocumentUploadAssistedMutation,
  useSubmitKycAssistedMutation,
  useReactivateUserMutation,
  useResetUserPasswordMutation,
  useRequestPasswordResetMutation,
  useConfirmPasswordResetMutation,
  useEraseUserMutation,
  // Delegation (WP-9). The jeweller's three, then the operator's two.
  useListDelegationGrantsQuery,
  useGrantDelegationMutation,
  useRevokeDelegationMutation,
  useListDelegationSessionsQuery,
  useListDelegableTenantsQuery,
  useMintDelegationTokenMutation
} = identityApi;

export { IDENTITY_MAX_PAGE_SIZE, IDENTITY_TAGS, KYC_DOCUMENT_RULES, SECOND_KYC_DOCUMENT_TYPES, changeMyPassword, fetchDelegableTenants, fetchMyKycStatus, fetchMySessions, identityApi, isAadhaarChecksumValid, isKycIdentifierValid, mintDelegationToken, normalizeKycIdentifier, revokeAllMySessions, revokeMySession, toKycUploadTicket, useAbandonSaverRegistrationMutation, useAssignRoleMutation, useChangeMyPasswordMutation, useClaimKycReviewMutation, useConfirmKycDocumentUploadAssistedMutation, useConfirmKycDocumentUploadMutation, useConfirmPasswordResetMutation, useDecideKycMutation, useEraseUserMutation, useGetKycDecisionHistoryQuery, useGetKycDocumentPolicyQuery, useGetKycDocumentsQuery, useGetMeQuery, useGetMyKycStatusQuery, useGetUserKycStatusQuery, useGetUserQuery, useGrantDelegationMutation, useListDelegableTenantsQuery, useListDelegationGrantsQuery, useListDelegationSessionsQuery, useListKycSubmissionsPageQuery, useListKycSubmissionsQuery, useListMyKycNoticesQuery, useListMySaversQuery, useListMySessionsQuery, useListUndeliverableKycNotificationsQuery, useListUsersPageQuery, useListUsersQuery, useMarkKycNoticeReadMutation, useMintDelegationTokenMutation, useProvisionPartnerUserMutation, useProvisionPlatformUserMutation, useReactivateUserMutation, useRegisterSaverAssistedMutation, useReleaseKycReviewMutation, useRequestKycDocumentUploadAssistedMutation, useRequestKycDocumentUploadMutation, useRequestPasswordResetMutation, useResendSaverOtpAssistedMutation, useResetUserPasswordMutation, useResolveUndeliverableKycNotificationMutation, useRevokeAllSessionsMutation, useRevokeDelegationMutation, useRevokeRoleMutation, useRevokeSessionMutation, useSubmitKycAssistedMutation, useSubmitKycMutation, useSuspendUserMutation, useUpdateAgentAttributionMutation, useUpdateMeMutation, useVerifySaverPhoneAssistedMutation };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map